/**
 * The note gutter of a page (#661): each note at the height of its block, joined to its frame by a line with a dot on the frame.
 *
 * Notes are measured (a `ResizeObserver` on each), not guessed: a note that has grown (a rung opened) pushes the notes below it down, and
 * their lines bend (`layoutNotes`, `connectorPath`: pure, in `feedback-blocks.ts`). Everything is in the sheet's own pixels, so the layer
 * moves and scales with the page. A note with no frame on the page has no line and sits below the others.
 */

import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { connectorPath, edges, GUTTER_GAP, GUTTER_WIDTH, layoutHeight, layoutNotes, type BoardNote, type Placement } from './feedback-blocks.js';
import type { PageBox } from './feedback-viewer.js';

export interface NotesLayerProps {
  readonly box: PageBox;
  readonly widthPx: number;
  /** The notes of this page, in reading order. */
  readonly notes: readonly BoardNote[];
  readonly rtl: boolean;
  readonly selectedId: string | undefined;
  readonly renderNote: (note: BoardNote) => React.JSX.Element;
  /** Anything that belongs at the top of the gutter (the "not checked yet" card), above every note. */
  readonly lead?: React.ReactNode;
}

/** The measured height of each element with a `data-note-id`, kept current. */
function useHeights(ids: readonly string[]): { readonly heights: ReadonlyMap<string, number>; readonly register: (id: string) => (element: HTMLElement | null) => void } {
  const elements = useRef(new Map<string, HTMLElement>());
  const [heights, setHeights] = useState<ReadonlyMap<string, number>>(new Map());
  const observer = useRef<ResizeObserver | null>(null);
  const read = (): void => {
    setHeights((now) => {
      let changed = now.size !== elements.current.size;
      const next = new Map<string, number>();
      for (const [id, el] of elements.current) {
        next.set(id, el.offsetHeight);
        if (now.get(id) !== el.offsetHeight) changed = true;
      }
      return changed ? next : now;
    });
  };
  const readRef = useRef(read);
  readRef.current = read;
  useLayoutEffect(() => {
    if (typeof ResizeObserver === 'undefined') {
      readRef.current();
      return undefined;
    }
    observer.current = new ResizeObserver(() => readRef.current());
    for (const el of elements.current.values()) observer.current.observe(el);
    readRef.current();
    return () => observer.current?.disconnect();
  }, []);
  // New notes are measured as soon as they are mounted.
  useLayoutEffect(() => {
    readRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the ids are the trigger
  }, [ids.join('|')]);
  const register = (id: string) => (element: HTMLElement | null): void => {
    const known = elements.current.get(id);
    if (element === null) {
      if (known !== undefined) observer.current?.unobserve(known);
      elements.current.delete(id);
      return;
    }
    if (known === element) return;
    if (known !== undefined) observer.current?.unobserve(known);
    elements.current.set(id, element);
    observer.current?.observe(element);
  };
  return { heights, register };
}

export function NotesLayer({ box, widthPx, notes, rtl, selectedId, renderNote, lead }: NotesLayerProps): React.JSX.Element {
  const scale = box.w > 0 && widthPx > 0 ? box.w / widthPx : 0;
  const ids = notes.map((note) => note.id);
  const { heights, register } = useHeights(lead === undefined ? ids : ['lead', ...ids]);
  const items: Placement[] = useMemo(
    () => [
      // An empty slot takes no room: the first note keeps the height of its block.
      ...((heights.get('lead') ?? 0) > 0 ? [{ id: 'lead', desiredTop: 0, height: heights.get('lead') ?? 0 }] : []),
      ...notes.map((note) => ({
        id: note.id,
        desiredTop: note.blockBbox === null ? null : note.blockBbox.y0 * scale,
        height: heights.get(note.id) ?? 0,
      })),
    ],
    [notes, heights, scale],
  );
  const tops = useMemo(() => layoutNotes(items), [items]);
  const total = layoutHeight(items, tops, box.h);
  const geometry = { gutterWidth: GUTTER_WIDTH, gap: GUTTER_GAP, pageWidth: box.w, rtl };
  return (
    <div className="fb-notes-layer" style={{ blockSize: `${String(total)}px` }}>
      <svg className="fb-connectors" aria-hidden="true" width="1" height={total} focusable="false">
        {notes.map((note, index) => {
          if (note.blockBbox === null) return null;
          const top = tops.get(note.id);
          if (top === undefined || scale === 0) return null;
          const frameTop = note.blockBbox.y0 * scale;
          const { noteEdgeX, frameEdgeX } = edges(geometry, note.blockBbox, scale);
          const { d, dot } = connectorPath({ noteEdgeX, frameEdgeX, noteTop: top, frameTop, frameHeight: (note.blockBbox.y1 - note.blockBbox.y0) * scale, index });
          return (
            <g key={note.id} className="fb-connector" data-kind={note.kind} data-selected={note.id === selectedId}>
              <path d={d} />
              <circle cx={dot.x} cy={dot.y} r={4.5} />
            </g>
          );
        })}
      </svg>
      {lead === undefined ? null : (
        <div ref={register('lead')} className="fb-note-slot" style={{ insetBlockStart: `${String(tops.get('lead') ?? 0)}px` }}>
          {lead}
        </div>
      )}
      {notes.map((note) => (
        <div key={note.id} ref={register(note.id)} className="fb-note-slot" data-note-id={note.id} style={{ insetBlockStart: `${String(tops.get(note.id) ?? 0)}px` }}>
          {renderNote(note)}
        </div>
      ))}
    </div>
  );
}
